import { Button, Dialog, IconButton, Tooltip, Typography } from '@mui/material'
import { Box } from '@mui/system'
import styled from '@emotion/styled'
import { Fragment, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import PersonIcon from '@mui/icons-material/Person'
import { useFormContext, useWatch } from 'react-hook-form'

import DialogTable from './dialogTable'
import { UserList } from '@models/user.model'
import Header from '@components/configurations/header'
import useTableView, { Sorting } from '@hooks/useTableView'
import UserService from '@services/users/user.service'
import useIsMounted from '@hooks/useIsMounted'
import { groupBy, merge } from 'lodash'
import { OptionsContext } from '@contexts/Options'
import { sortByName } from '@utils/sortByName'
import DialogContent from './dialog/dialogContent'
import { useParams } from 'react-router-dom'
import { userInfo } from 'os'

const FormBox = styled(Box)(() => ({
	display: 'flex',
	flexDirection: 'column',
	gap: '12px',
}))

const SimpleDialog = ({
	open,
	onConfirm,
	onClose,
	list,
	selectedUsers,
	setSelectedUsers,
	setInputValueSearched,
	inputValueSearched,
	handleInputSearch,
}: any) => (
	<Dialog onClose={onClose} open={open} fullWidth maxWidth="md">
		<Box
			display="flex"
			justifyContent={'space-between'}
			alignItems="center"
			sx={{ padding: '16px 25px 16px 25px', backgroundColor: '#F6F6F6', color: '#707070' }}
		>
			<Typography variant="h3">Add Users</Typography>
			<IconButton onClick={onClose}>
				<CloseIcon />
			</IconButton>
		</Box>
		{/* <DialogTable /> */}
		<DialogContent
			list={list}
			selectedUsers={selectedUsers}
			setSelectedUsers={setSelectedUsers}
			setInputValueSearched={setInputValueSearched}
			inputValueSearched={inputValueSearched}
			handleInputSearch={handleInputSearch}
		/>
		<Box
			display="flex"
			justifyContent={'flex-end'}
			alignItems="center"
			sx={{ padding: '16px', backgroundColor: '#F6F6F6' }}
			gap="12px"
		>
			<Button variant="contained" color="defaultButton" onClick={onConfirm}>
				Confirm
			</Button>
			<Button variant="contained" color="error" onClick={onClose}>
				Cancel
			</Button>
		</Box>
	</Dialog>
)

const AddingUserForm = () => {
	const { t } = useTranslation()

	const { control, setValue, getValues } = useFormContext()
	const roleProfileIds = useWatch({ control, name: 'role_profile_ids' })

	const { state, dispatch } = useContext(OptionsContext)

	const [open, setOpen] = useState(false)
	const refMounted = useRef(true)
	const { project_id } = useParams<string>()
	const [selectedUsers, setSelectedUsers] = useState<any>([])
	const [initialSelectedUsers, setInitialSelectedUsers] = useState<any>([])
	const [dialogList, setDialogList] = useState<any>(null)
	const [inputValueSearched, setInputValueSearched] = useState<string>('')
	// const userIds = useWatch({ control, name: })
	console.debug('getValues: ', getValues())

	useEffect(() => {
		console.log('useEffect reload roleProfileIds', roleProfileIds)
		reload()
	}, [roleProfileIds])

	const handleClickOpen = () => {
		setOpen(true)
		getDialogDetails(null)
	}

	const handleConfirm = () => {
		setOpen(false)
		setValue('role_profile_ids', [...selectedUsers])
	}

	const handleClose = () => {
		setOpen(false)
		setSelectedUsers([...initialSelectedUsers])
		setInputValueSearched('')
	}

	const getDialogDetails = async (searchedUserListPerProject: any) => {
		const getUsersSearchRequest: any = {
			// search: [""],
			project_ids: {
				operator: '=',
				value: [project_id],
			},
		}

		let usersList
		if (!searchedUserListPerProject) {
			usersList = await UserService.GetUserList(getUsersSearchRequest)
		}

		const _list = (searchedUserListPerProject || usersList).data.map((user: any) => {
			const data = selectedUsers.filter((selectedUserId: string) => {
				return user.id === selectedUserId
			})
			if (data?.length) {
				return {
					...user,
					isSelected: true,
				}
			} else {
				return {
					...user,
					isSelected: false,
				}
			}
		})
		const _listData = groupBy(_list, 'company')
		const _userListPerCompany = []
		for (const [key, value] of Object.entries(_listData)) {
			_userListPerCompany.push({ title: key, contents: value })
		}
		setDialogList(_userListPerCompany)
	}

	const handleInputSearch = async (inputValue: string) => {
		const payload = {
			search: [inputValue],
			project_ids: { operator: '=', value: [project_id || ''] },
		}
		const searchedUserListPerProject = await UserService.GetUserList(payload)

		getDialogDetails(searchedUserListPerProject)
	}

	const [TableView, reload] = useTableView<UserList>({
		columns: [
			{
				key: 'last_name',
				desc: t('Role Name'),
				disableSort: false,
				renderCell: (params: UserList) => {
					return (
						<Fragment>
							<Typography variant="body2">
								{params?.first_name + ' ' + params?.last_name}
							</Typography>
						</Fragment>
					)
				},
			},
			{
				key: 'company',
				desc: t('Organisation'),
				disableSort: false,
				renderCell: (params: UserList) => {
					return (
						<Fragment>
							<Typography variant="body2">{params.company?.length > 18}</Typography>
						</Fragment>
					)
				},
			},
			{
				key: 'position',
				desc: t('Position'),
				disableSort: false,
				renderCell: (params: UserList) => {
					return (
						<Fragment>
							<Typography variant="body2">{params.position}</Typography>
						</Fragment>
					)
				},
			},
			{
				key: 'email',
				desc: t('Email'),
				disableSort: false,
				renderCell: (params: UserList) => {
					return (
						<Fragment>
							<Typography variant="body2">{params.email}</Typography>
						</Fragment>
					)
				},
			},
			{
				key: 'phone',
				desc: t('Phone'),
				disableSort: false,
				renderCell: (params: UserList) => {
					return (
						<Fragment>
							<Typography variant="body2">{params.phone}</Typography>
						</Fragment>
					)
				},
			},
			{
				key: 'id',
				desc: t('Function Buttons'),
				disableSort: false,
				renderCell: (params: UserList) => {
					return (
						<Tooltip title="Remove">
							<IconButton
								onClick={() => {
									let new_value = roleProfileIds?.filter((user: string) => user !== params.id)
									setValue('role_profile_ids', new_value, { shouldDirty: true })

									let _index = [...selectedUsers].indexOf(params.id)
									if (_index !== -1) {
										let _selectedUsers = [...selectedUsers]
										_selectedUsers.splice(_index, 1)
										setSelectedUsers(_selectedUsers)
										setInitialSelectedUsers(_selectedUsers)
									}
								}}
							>
								<CloseIcon />
							</IconButton>
						</Tooltip>
					)
				},
			},
		],
		reloadCallback: async (
			page?: number,
			page_size?: number,
			filter?: any,
			search?: any,
			sorting?: Sorting,
		) => {
			if (refMounted.current && roleProfileIds && roleProfileIds.length > 0) {
				console.log('dllm reload la', roleProfileIds)
				let _filter =
					roleProfileIds.length > 0
						? { ...filter, profile_ids: { operator: '=', value: roleProfileIds } }
						: filter
				let req = merge(_filter, {
					pagination: {
						page: page ?? 0,
						page_size: page_size,
					},
					search: search,
					sort: sorting,
				})

				let roleData = await UserService.GetUserList(req)

				setSelectedUsers(roleProfileIds)
				setInitialSelectedUsers(roleProfileIds)

				let optionsList = await UserService.GetUsersOptions({
					...filter,
					profile_ids: { operator: '=', value: roleProfileIds },
				})

				let new_criterion = [
					{
						key: 'name',
						name: t('Name'),
						type: 'StringFilter',
					},
					{
						key: 'org_ids',
						name: t('Organisation'),
						type: 'SelectionFilter',
						options: state.organization,
					},
					{
						key: 'project_ids',
						name: t('Projects'),
						type: 'SelectionFilter',
						// options: state.project,
						options: sortByName(state.project)?.filter((elem: any) => {
							return optionsList.project_ids?.includes(elem.key)
						}),
					},
					{
						key: 'position',
						name: t('Dept/Team'),
						type: 'StringFilter',
					},
					{
						key: 'email',
						name: t('Email'),
						type: 'StringFilter',
					},
					{
						key: 'phone',
						name: t('Phone'),
						type: 'StringFilter',
					},
					{
						key: 'active',
						name: t('Active'),
						type: 'ActiveFilter',
					},
				]

				let results: { data: UserList[]; total_count: number; criterion: any } = {
					...roleData,
					criterion: new_criterion,
				}

				return results
			}
		},
		toolbar: true,
		mountedRef: refMounted,
		tableHeight: '330px',
		rowsPerPage: 25,
		additionButton: (
			<Button variant="contained" onClick={handleClickOpen} color="defaultButton">
				{t('Add Users')}
			</Button>
		),
		criterion: [
			{
				key: 'name',
				name: t('Name'),
				type: 'StringFilter',
			},
			{
				key: 'org_ids',
				name: t('Organisation'),
				type: 'SelectionFilter',
				options: state.organization,
			},
			{
				key: 'project_ids',
				name: t('Projects'),
				type: 'SelectionFilter',
				options: state.project,
			},
			{
				key: 'position',
				name: t('Position'),
				type: 'StringFilter',
			},
			{
				key: 'email',
				name: t('Email'),
				type: 'StringFilter',
			},
			{
				key: 'phone',
				name: t('Phone'),
				type: 'StringFilter',
			},
			{
				key: 'active',
				name: t('Active'),
				type: 'ActiveFilter',
			},
		],
		defaultFilterCriteria: { active: { operator: '=', value: [true] } },
		renderNoResult: false,
	})

	return (
		<Box
			sx={{
				marginTop: '30px',
			}}
		>
			<Header
				title={t('Adding User')}
				icon={<PersonIcon sx={{ fontSize: 40, color: '#707070' }} />}
			/>
			<FormBox>
				<SimpleDialog
					open={open}
					onConfirm={handleConfirm}
					onClose={handleClose}
					list={dialogList}
					selectedUsers={selectedUsers}
					setSelectedUsers={setSelectedUsers}
					setInputValueSearched={setInputValueSearched}
					inputValueSearched={inputValueSearched}
					handleInputSearch={handleInputSearch}
				/>
				<TableView />
			</FormBox>
		</Box>
	)
}

export default AddingUserForm
